import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import ssmQr from '../../assets/images/ssm-qr.png';
import { 
  Mail, Phone, Send, CheckCircle2, MessageSquare,
  Building2, Users, Home as HomeIcon, ArrowRight
} from 'lucide-react';

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    hotelName: '',
    email: '',
    phone: '',
    numberOfProperties: '',
    numberOfRooms: '',
    message: '',
    requestType: 'inquiry' // 'inquiry' or 'demo'
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(window.location.search);
    const subject = params.get('subject');
    const type = params.get('type');
    if (subject || type) {
      setFormData(prev => ({
        ...prev,
        message: subject ? `${subject}\n\n` : prev.message,
        requestType: type === 'demo' ? 'demo' : prev.requestType,
      }));
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e, type) => {
    e.preventDefault();
    setLoading(true);
    
    const submitData = { ...formData, requestType: type };
    // Normalize origin to apex domain to avoid www → apex 307 redirect that breaks CORS
    const apiBase = window.location.origin.replace('://www.', '://');
    
    try {
      const response = await fetch(`${apiBase}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });
      
      if (response.ok) {
        setSubmitted(true);
        setFormData({
          fullName: '',
          hotelName: '',
          email: '',
          phone: '',
          numberOfProperties: '',
          numberOfRooms: '',
          message: '',
          requestType: 'inquiry'
        });
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.detail || 'Sorry — we could not send your message. Please email support@staymii.com directly.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      alert('Sorry — a network error occurred. Please email support@staymii.com directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Contact Us - Get in Touch"
        description="Contact StayMii team for hotel PMS demo, pricing inquiries, or support. Based in Penang, Malaysia. Email: support@staymii.com"
        keywords="contact staymii, hotel software demo, hotel pms support malaysia, staymii penang"
        canonicalUrl="/contact"
      />
      
      {/* Navigation */}
      <LandingNavBar />

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#0F172A] to-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-white/90 font-medium mb-4">
            Get in touch with the StayMii team.
          </p>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            Interested in StayMii for your hotel? Contact us to learn more, request a demo, or speak with our team.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {submitted ? (
            /* Success Message */
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8 lg:p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4">
                Thank you. Our team will contact you shortly.
              </h2>
              <p className="text-slate-600 mb-8">
                We've received your inquiry and will get back to you within 24 hours.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="px-6 py-3 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl font-semibold transition-all"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            /* Contact Form */
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8 lg:p-12">
              <div className="text-center mb-10">
                <div className="w-16 h-16 bg-[#2563EB]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Send className="h-8 w-8 text-[#2563EB]" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Send Us a Message</h2>
                <p className="text-slate-600">Fill out the form below and we'll get back to you soon.</p>
              </div>

              <form className="space-y-6">
                {/* Row 1: Full Name & Hotel Name */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-slate-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none transition-all"
                      data-testid="contact-fullname-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="hotelName" className="block text-sm font-medium text-slate-700 mb-2">
                      Hotel / Company Name *
                    </label>
                    <input
                      type="text"
                      id="hotelName"
                      name="hotelName"
                      value={formData.hotelName}
                      onChange={handleChange}
                      required
                      placeholder="Grand Hotel Kuala Lumpur"
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none transition-all"
                      data-testid="contact-hotel-input"
                    />
                  </div>
                </div>

                {/* Row 2: Email & Phone */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="you@hotel.com"
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none transition-all"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+60 12-345 6789"
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none transition-all"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                {/* Row 3: Number of Properties & Rooms */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="numberOfProperties" className="block text-sm font-medium text-slate-700 mb-2">
                      Number of Properties
                    </label>
                    <select
                      id="numberOfProperties"
                      name="numberOfProperties"
                      value={formData.numberOfProperties}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none transition-all bg-white"
                      data-testid="contact-properties-select"
                    >
                      <option value="">Select...</option>
                      <option value="1">1 Property</option>
                      <option value="2-5">2-5 Properties</option>
                      <option value="6-10">6-10 Properties</option>
                      <option value="11-20">11-20 Properties</option>
                      <option value="20+">20+ Properties</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="numberOfRooms" className="block text-sm font-medium text-slate-700 mb-2">
                      Number of Rooms
                    </label>
                    <select
                      id="numberOfRooms"
                      name="numberOfRooms"
                      value={formData.numberOfRooms}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none transition-all bg-white"
                      data-testid="contact-rooms-select"
                    >
                      <option value="">Select...</option>
                      <option value="1-10">1-10 Rooms</option>
                      <option value="11-30">11-30 Rooms</option>
                      <option value="31-50">31-50 Rooms</option>
                      <option value="51-100">51-100 Rooms</option>
                      <option value="100+">100+ Rooms</option>
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Tell us about your hotel and what you're looking for..."
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none transition-all resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e, 'inquiry')}
                    disabled={loading}
                    className="flex-1 px-6 py-4 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="submit-inquiry-btn"
                  >
                    <Send className="h-5 w-5" />
                    {loading ? 'Sending...' : 'Submit Inquiry'}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e, 'demo')}
                    disabled={loading}
                    className="flex-1 px-6 py-4 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="request-demo-btn"
                  >
                    <ArrowRight className="h-5 w-5" />
                    {loading ? 'Sending...' : 'Request Demo'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Contact Information */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="bg-slate-50 rounded-2xl p-6 flex items-start gap-4">
              <div className="w-12 h-12 bg-[#2563EB]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Building2 className="h-6 w-6 text-[#2563EB]" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1">Company</h3>
                <div className="flex items-start gap-4">
                  <div>
                    <p className="text-slate-700 font-medium">Mii Global Partner Holdings Sdn Bhd</p>
                    <p className="text-sm text-slate-500">(202601028203 (1690298-K))</p>
                    <p className="text-sm text-slate-500 mt-2">11, Taman Bukit Dumbar,</p>
                    <p className="text-sm text-slate-500">11600, Jelutong, Penang.</p>
                  </div>
                  <img src={ssmQr} alt="SSM BizTrust - Mii Global Partner Holdings Sdn Bhd" className="w-24 h-24 object-contain rounded-lg border border-slate-200" data-testid="ssm-qr-code" />
                </div>
              </div>
            </div>
            <div className="bg-slate-50 rounded-2xl p-6 flex items-start gap-4">
              <div className="w-12 h-12 bg-[#2563EB]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Mail className="h-6 w-6 text-[#2563EB]" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Email Us</h3>
                <a href="mailto:support@staymii.com" className="text-[#2563EB] hover:underline">
                  support@staymii.com
                </a>
                <p className="text-sm text-slate-500 mt-1">We'll respond within 24 hours</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-2xl p-6 flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <MessageSquare className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">WhatsApp</h3>
                <a href="https://wa.me/60103687001" className="text-green-600 hover:underline">
                  +60 10-368 7001
                </a>
                <p className="text-sm text-slate-500 mt-1">Chat with us directly</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <LandingFooter />
    </div>
  );
};

export default Contact;
